/**
 * Chữ web quầy theo NHÓM NGÀNH của tiệm (kế hoạch 10/2026, 4.3; chủ dự án duyệt Q5): tiệm thú cưng / studio / rửa xe
 * không đọc "kỹ thuật viên", "liệu trình", "sơn gel" rồi tin ứng dụng không phải của mình.
 *
 * - Nhóm lấy từ danh mục ngành một nguồn: `NGANH[nganh].nhom` (`packages/shared/src/nganh.ts`, ADR-082).
 * - `lam-dep` GIỮ NGUYÊN từng chữ như trước. `dich-vu` đổi sang chữ trung tính ("Nhân viên", "Gói nhiều lần", ví dụ chung).
 * - KHÔNG đổi vai trong DB (`technician` vẫn là `technician`), KHÔNG i18n (NOT-DOING #11): chỉ là hai bộ chữ tiếng Việt.
 *
 * Cách dùng: `datNganh(toi.nganh)` MỘT lần ngay khi biết tiệm (đăng nhập / đổi ngành); các chuỗi trong `text/*.ts` là
 * `get` đọc `chuHienTai()` lúc VẼ nên mọi chỗ gọi `T.detailStaff`… không phải sửa.
 */
import { chuKhoangPhut, laNganhTiem, NGANH, type NhomNganh } from '@sapa/shared'

export interface ChuNhom {
  /** Tên vai `technician` + người làm dịch vụ. */
  nhanVien: string
  moiNhanVien: string
  themNhanVienGoiY: string
  khachVangLaiGoiY: string
  hoaHongNhanVien: string
  /** Tên mục "gói bán trước nhiều buổi". */
  goiNhieuLan: string
  viDuTenDichVu: string
  viDuTenGoi: string
  moTaSanPham: string
  viDuTenSanPham: string
  viDuGhiChuBuoi: string
  viDuKhoanChi: string
}

const LAM_DEP: ChuNhom = {
  nhanVien: 'Kỹ thuật viên',
  moiNhanVien: 'Mọi kỹ thuật viên',
  themNhanVienGoiY: 'Thêm kỹ thuật viên để khách đặt được giờ của họ.',
  khachVangLaiGoiY: 'Khách đến không hẹn — gợi ý kỹ thuật viên theo lượt',
  hoaHongNhanVien: 'Hoa hồng kỹ thuật viên',
  goiNhieuLan: 'Gói liệu trình',
  viDuTenDichVu: 'ví dụ: Gội đầu dưỡng sinh',
  viDuTenGoi: 'ví dụ: Massage 10 tặng 2',
  moTaSanPham:
    'Thuốc nhuộm, gel, dầu gội… Mỗi sản phẩm chỉ một đơn vị (ml, g, chai, cái). Có giá bán lẻ thì bán được ở quầy.',
  viDuTenSanPham: 'ví dụ: Thuốc nhuộm nâu',
  viDuGhiChuBuoi: 'Ví dụ: sơn gel đỏ đô, móng vuông, khách thích dài 1cm',
  viDuKhoanChi: 'ví dụ: mua 5 lọ sơn gel',
}

/** Nhóm "dịch vụ" (thú cưng, studio, rửa xe, khác): chữ chung, không gợi một nghề cụ thể. */
const DICH_VU: ChuNhom = {
  nhanVien: 'Nhân viên',
  moiNhanVien: 'Mọi nhân viên',
  themNhanVienGoiY: 'Thêm nhân viên để khách đặt được giờ của họ.',
  khachVangLaiGoiY: 'Khách đến không hẹn — gợi ý nhân viên theo lượt',
  hoaHongNhanVien: 'Hoa hồng nhân viên',
  goiNhieuLan: 'Gói nhiều lần',
  viDuTenDichVu: 'ví dụ: Dịch vụ cơ bản',
  viDuTenGoi: 'ví dụ: Mua 10 buổi tặng 2',
  moTaSanPham:
    'Vật tư dùng trong dịch vụ hoặc hàng bán kèm. Mỗi sản phẩm chỉ một đơn vị (ml, g, chai, cái). Có giá bán lẻ thì bán được ở quầy.',
  viDuTenSanPham: 'ví dụ: Khăn lau',
  viDuGhiChuBuoi: 'Ví dụ: khách thích làm nhẹ tay, lần sau nhắc trước một ngày',
  viDuKhoanChi: 'ví dụ: mua 5 chai nước rửa',
}

export function chuTheoNhom(nhom: NhomNganh): ChuNhom {
  return nhom === 'dich-vu' ? DICH_VU : LAM_DEP
}

/** Chưa biết ngành (chưa đăng nhập) → bộ chữ cũ, đúng như trước khi có tệp này. */
let nhomHienTai: NhomNganh = 'lam-dep'

/**
 * Báo ngành của tiệm đang đăng nhập. Chuỗi lạ (máy chủ cũ, ngành mới chưa có ở bản web này) → giữ nhóm hiện tại,
 * không đoán.
 */
export function datNganh(nganh: string | null | undefined): void {
  if (nganh !== null && nganh !== undefined && laNganhTiem(nganh)) nhomHienTai = NGANH[nganh].nhom
}

export function nhomDangDung(): NhomNganh {
  return nhomHienTai
}

export function chuHienTai(): ChuNhom {
  return chuTheoNhom(nhomHienTai)
}

/**
 * Tên vai hiển thị: máy chủ trả sẵn `tenVai` ("Kỹ thuật viên" cho `technician`); nhóm dịch vụ thấy "Nhân viên".
 * Vai khác (chủ tiệm, quản lý, lễ tân) giữ nguyên.
 */
export function tenVaiHienThi(vai: string, tenVai: string): string {
  return vai === 'technician' ? chuHienTai().nhanVien : tenVai
}

// ===================================================================== 4.2 — Khách đặt trước tối đa
export const DAT_TRUOC = {
  nhan: 'Khách đặt trước tối đa',
  goiY: 'Khách chỉ chọn được ngày trong khoảng này. Quầy vẫn xếp lịch xa hơn được.',
  soNgay: (n: number) => `${n} ngày`,
  /** Cho đọc màn hình: chip đang chọn. */
  dangChon: (n: number) => `Đang chọn: ${n} ngày`,
} as const

/** Năm mức chủ tiệm chọn (kế hoạch 4.2). Giá trị khác đang có ở DB (1..365) vẫn hiện thêm một chip để không bị mất. */
export const MUC_DAT_TRUOC_NGAY = [30, 45, 60, 90, 180] as const
export const DAT_TRUOC_MAC_DINH_NGAY = 45

// ===================================================================== K5 — Hạn đổi / huỷ và đặt trước ít nhất
/**
 * Hai luật tiệm đặt cho khách (K5, ADR-099). Mỗi luật là một hàng chip (không ô gõ số): chủ tiệm chọn một mức, máy
 * chủ kiểm khoảng hợp lệ. Giá trị lạ ở DB (vd 90) vẫn hiện thêm một chip — không mất lựa chọn đang dùng.
 */
export const DAT_TOI_THIEU = {
  nhan: 'Khách đặt trước ít nhất',
  goiY: 'Khách không chọn được giờ sát hơn mức này. Quầy vẫn xếp lịch sát giờ được.',
} as const

export const HAN_DOI_HUY = {
  nhan: 'Khách tự đổi giờ hoặc huỷ trên Zalo, tới trước giờ hẹn',
  goiY: 'Sát hơn mức này, khách phải nhắn hoặc gọi tiệm. Áp dụng cả cho lịch khách đã đặt.',
} as const

/** Tiêu đề nhóm đặt trên ba luật khách đặt lịch của chi nhánh (đặt trước tối đa · ít nhất · tự đổi / huỷ). */
export const LUAT_DAT_LICH = {
  tieuDe: 'Luật đặt lịch',
} as const

export const MUC_DAT_TOI_THIEU_PHUT = [0, 30, 60, 120, 1440] as const
export const MUC_HAN_DOI_HUY_PHUT = [0, 60, 120, 1440, 2880] as const
/** Khớp DEFAULT của `branch` (0002): máy chủ cũ không trả số thì coi như mặc định. */
export const DAT_TOI_THIEU_MAC_DINH_PHUT = 30
export const HAN_DOI_HUY_MAC_DINH_PHUT = 120

export function chuDatToiThieu(phut: number): string {
  return phut === 0 ? 'Sát giờ cũng được' : chuKhoangPhut(phut)
}

export function chuHanDoiHuy(phut: number): string {
  // Nhãn nhóm đã nói "tới trước giờ hẹn": chip chỉ là số ("2 tiếng"), không thêm "Trước" (đọc thành sau hạn).
  return phut === 0 ? 'Sát giờ hẹn' : chuKhoangPhut(phut)
}

/** Các chip của một hàng: các mức cố định + giá trị đang có (nếu lạ), tăng dần. */
export function cacChipChinhSach(
  muc: readonly number[],
  dangCo: number,
  chu: (phut: number) => string,
): { phut: number; chu: string }[] {
  return [...new Set<number>([...muc, dangCo])]
    .sort((a, b) => a - b)
    .map((phut) => ({ phut, chu: chu(phut) }))
}
